import * as React from 'react';

import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { ApolloProvider } from '@apollo/client';

import AppContent from 'AppContent/AppContent';
import { getApolloClient } from 'common/apollo/client';
import { DialogProvider } from 'common/components/core/DialogProvider';
import GlobalLoaderProvider from 'common/components/GlobalLoader/GlobalLoaderProvider';
import LiveAnnouncerProvider from 'common/components/LiveAnnouncer/LiveAnnouncerProvider';
import OfflineStateProvider from 'common/components/OfflineState/OfflineStateProvider';
import NavigationStackProvider from 'common/components/withNavigationStack/NavigationStackProvider';
import PermissionsProvider from 'common/components/withPermissions/PermissionsProvider';
import { PollingContextProvider } from 'common/components/withPollingContext/PollingContextProvider';
import { withUIStateProvider } from 'common/components/withUIState/withUIStateProvider';
import UnsavedChangesProvider from 'common/components/withUnsavedChanges/withUnsavedChangesProvider';
import ErrorBoundary from 'ErrorBoundary/ErrorBoundary';

import ThemeProvider from './common/components/core/Theme/ThemeProvider';

export interface AppProps {
  shouldLogin: boolean;
}

interface AppState {
  client: ApolloClient<NormalizedCacheObject> | null;
}

class App extends React.Component<AppProps, AppState> {
  public readonly state = {
    client: null,
  };

  /**
   * Before component mounts we have to prepare Apollo client
   */
  public async componentDidMount() {
    const client = await getApolloClient();
    this.setState({ client });
  }

  public render() {
    return (
      this.state.client && (
        <ThemeProvider>
          <LiveAnnouncerProvider>
            <UnsavedChangesProvider>
              <NavigationStackProvider>
                <GlobalLoaderProvider>
                  <DialogProvider>
                    <ApolloProvider client={this.state.client}>
                      <ErrorBoundary>
                        <PollingContextProvider>
                          <PermissionsProvider>
                            <OfflineStateProvider>
                              <AppContent shouldLogin={this.props.shouldLogin} />
                            </OfflineStateProvider>
                          </PermissionsProvider>
                        </PollingContextProvider>
                      </ErrorBoundary>
                    </ApolloProvider>
                  </DialogProvider>
                </GlobalLoaderProvider>
              </NavigationStackProvider>
            </UnsavedChangesProvider>
          </LiveAnnouncerProvider>
        </ThemeProvider>
      )
    );
  }
}

export default withUIStateProvider(App) as unknown as React.ComponentType<AppProps>;
